import Seo from "@/components/seo/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ContactForm from "@/components/feature/ContactForm";
import { SITE_INFO } from "@/mocks/data";

const stats = [
  { value: SITE_INFO.members, label: "注册同好", icon: "ri-user-heart-line" },
  { value: SITE_INFO.articles, label: "优质书评", icon: "ri-article-line" },
  { value: SITE_INFO.categories, label: "漫画类型", icon: "ri-book-2-line" },
  { value: "1,200+", label: "粉丝圈子", icon: "ri-group-2-line" },
];

const values = [
  { icon: "ri-discuss-line", title: "真诚讨论", desc: "尊重每一种观点，让讨论有温度、有深度。" },
  { icon: "ri-fire-line", title: "热爱安利", desc: "把真正值得一看的宝藏漫画，安利给更多人。" },
  { icon: "ri-heart-line", title: "同好至上", desc: "以同好为核心，打造有归属感的粉丝社区。" },
  { icon: "ri-rocket-line", title: "持续进步", desc: "不断优化内容与体验，陪伴每一位追更人。" },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "关于漫画岛",
  url: `${SITE_INFO.domain}/about`,
  description: `关于漫画岛：${SITE_INFO.description}`,
  isPartOf: { "@type": "WebSite", name: SITE_INFO.name, url: SITE_INFO.domain },
  inLanguage: "zh-CN",
};

export default function AboutPage() {
  return (
    <>
      <Seo
        title="关于我们"
        description={`关于漫画岛品牌介绍：${SITE_INFO.description}。了解我们的口碑、理念与联系方式，加入 ${SITE_INFO.members} 同好一起聊剧情、求推荐、找同好一起追更。`}
        keywords="漫画岛,关于我们,漫画岛品牌介绍,漫画岛口碑,漫画同好社区"
        canonicalPath="/about"
        jsonLd={jsonLd}
      />

      {/* Banner */}
      <section className="relative w-full overflow-hidden" aria-label="关于我们">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=stylized%20manga%20illustration%20of%20a%20group%20of%20comic%20fans%20reading%20and%20discussing%20together%20around%20books%2C%20warm%20terracotta%20and%20amber%20tones%2C%20cozy%20community%20atmosphere%2C%20dark%20gradient%20overlay%20for%20text&width=1600&height=520&seq=about-hero-2026&orientation=landscape"
            alt="漫画岛同好一起阅读讨论的场景插画"
            title="关于漫画岛"
            className="h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/35 to-black/40"></div>
        </div>
        <div className="relative mx-auto w-full max-w-7xl px-4 py-16 md:px-6 md:py-24">
          <Breadcrumb items={[{ label: "关于我们" }]} />
          <h1 className="mt-6 font-heading text-3xl font-black text-background-50 md:text-4xl">关于漫画岛</h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-100 md:text-base">
            一个属于漫画爱好者的同好讨论社区。自 {SITE_INFO.founded} 年成立以来，我们一直相信：好看的漫画，值得被认真讨论与安利。
          </p>
        </div>
      </section>

      {/* 品牌介绍 */}
      <section className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6" aria-label="品牌介绍">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
          <div className="overflow-hidden rounded-lg border border-background-200">
            <img
              src="https://readdy.ai/api/search-image?query=warm%20manga%20illustration%20of%20a%20cozy%20comic%20bookstore%20with%20shelves%20full%20of%20comics%2C%20fans%20chatting%20happily%2C%20terracotta%20and%20cream%20palette%2C%20inviting%20community%20vibe&width=900&height=680&seq=about-intro-2026&orientation=portrait"
              alt="漫画岛同好讨论的温暖场景"
              title="漫画岛品牌介绍"
              className="h-full w-full object-cover object-center"
            />
          </div>
          <div>
            <span className="flex items-center gap-2 text-sm font-medium text-primary-700">
              <i className="ri-flag-2-line" aria-hidden="true"></i> 品牌介绍
            </span>
            <h2 className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-3xl">把好看漫画，安利给懂的人</h2>
            <div className="mt-5 flex flex-col gap-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              <p>
                漫画岛诞生于对漫画的纯粹热爱。我们聚集了 {SITE_INFO.members} 名同好，通过评论区、粉丝圈子与书评交流，让每一部好漫画都不被埋没。这里既有深度书评人的专业解析，也有普通读者的真实心声。
              </p>
              <p>
                我们覆盖热血冒险、恋爱日常、玄幻仙侠、悬疑烧脑、搞笑治愈与科幻未来六大类型，聚合热门漫画安利与追更讨论。看完漫画来聊剧情、求推荐、找同好一起追更——这正是漫画岛存在的意义。
              </p>
              <p>
                无论你是资深漫迷还是刚入坑的新人，漫画岛都欢迎你。安利墙随时互动，同好互助推荐，在这里，你总能找到懂你的人。
              </p>
            </div>
            <div className="mt-7 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {stats.map((s) => (
                <div key={s.label} className="rounded-lg border border-background-200 bg-background-100 p-4 text-center">
                  <i className={`${s.icon} text-xl text-primary-600`} aria-hidden="true"></i>
                  <p className="mt-2 font-heading text-lg font-bold text-foreground-950">{s.value}</p>
                  <p className="mt-0.5 text-xs text-foreground-500">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 口碑与理念 */}
      <section className="bg-background-100" aria-label="口碑与理念">
        <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6">
          <div className="text-center">
            <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">同好口碑，源于用心</h2>
            <p className="mt-2 text-sm text-foreground-600">四大理念，支撑起这个温暖的社区</p>
          </div>
          <div className="mt-9 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v) => (
              <div key={v.title} className="rounded-lg border border-background-200 bg-background-50 p-6 text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-2xl text-primary-700">
                  <i className={v.icon} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-900">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 overflow-hidden rounded-lg border border-background-200 bg-background-50">
            <blockquote className="flex flex-col gap-4 p-7 text-center sm:flex-row sm:text-left">
              <i className="ri-double-quotes-l text-3xl text-accent-500" aria-hidden="true"></i>
              <div>
                <p className="font-heading text-lg font-bold text-foreground-900">
                  「这里是漫画爱好者的家，看完漫画来聊剧情、求推荐、找同好一起追更。」
                </p>
                <footer className="mt-3 text-sm text-foreground-500">—— 漫画岛全体同好 · {SITE_INFO.founded} 年至今</footer>
              </div>
            </blockquote>
          </div>
        </div>
      </section>

      {/* 联系 */}
      <section className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6" aria-label="联系我们">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div>
            <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">联系我们</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              无论是内容合作、意见反馈，还是想加入我们分享书评，都欢迎通过下方方式与我们联系。我们会尽快回复你。
            </p>
            <ul className="mt-7 flex flex-col gap-4">
              <li className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-lg text-primary-700"><i className="ri-mail-line" aria-hidden="true"></i></span>
                <div>
                  <p className="text-sm font-medium text-foreground-900">商务邮箱</p>
                  <p className="mt-1 text-sm text-foreground-600">{SITE_INFO.contact}</p>
                </div>
              </li>
              <li className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-lg text-primary-700"><i className="ri-phone-line" aria-hidden="true"></i></span>
                <div>
                  <p className="text-sm font-medium text-foreground-900">客服热线</p>
                  <p className="mt-1 text-sm text-foreground-600">{SITE_INFO.phone}（工作日 9:00 - 18:00）</p>
                </div>
              </li>
              <li className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-lg text-primary-700"><i className="ri-map-pin-2-line" aria-hidden="true"></i></span>
                <div>
                  <p className="text-sm font-medium text-foreground-900">公司地址</p>
                  <p className="mt-1 text-sm text-foreground-600">{SITE_INFO.address}</p>
                </div>
              </li>
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-chat-smile-3-line text-primary-600" aria-hidden="true"></i> 给我们留言
            </h3>
            <p className="mt-2 text-sm text-foreground-600">填写下方表单，我们会第一时间与你取得联系。</p>
            <div className="mt-5">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}